import { Booking, ExpertTime, SalonDate } from '../models/booking';
import { StringHHmm, StringYYYYMMDD, StringYYYYMMDDHHmm } from '../models/date-time';
import { toStringHHmm, toStringYYYYMMDD, toStringYYYYMMDDHHmm } from './date-type-functions';
import { DateUtils } from './date-utils';

export class BookingUtils {
  public static assembleExpertTime(expertId: string, time: StringHHmm): ExpertTime {
    if (!expertId || !time) {
      throw new Error('Invalid expertId or time');
    }
    return `${expertId}:${time}` as ExpertTime;
  }

  public static assembleSalonDate(salonId: string, date: StringYYYYMMDD): SalonDate {
    if (!salonId || !date) {
      throw new Error('Invalid salonId or date');
    }
    return `${salonId}:${date}` as SalonDate;
  }

  public static getSlotYYYYMMDDHHmmOfSalonDateAndExpertTime(salonDate: SalonDate, expertTime: ExpertTime): StringYYYYMMDDHHmm {
    const date = BookingUtils.getDateOfSalonDate(salonDate);
    const time = BookingUtils.getTimeOfExpertTime(expertTime);
    return toStringYYYYMMDDHHmm(`${date}${time}`);
  }

  public static getExpertIdOfExpertTime(expertTime: ExpertTime): string {
    return expertTime.split(':')[0];
  }

  public static getTimeOfExpertTime(expertTime: ExpertTime): StringHHmm {
    return toStringHHmm(expertTime.split(':')[1]);
  }

  public static getDateOfSalonDate(salonDate: SalonDate): StringYYYYMMDD {
    return toStringYYYYMMDD(salonDate.split(':')[1]);
  }

  public static getSalonIdOfSalonDate(salonDate: SalonDate): string {
    return salonDate.split(':')[0];
  }

  // booking => '24.03.2026 14:30' or '24/4/2026 15:15' (locale-dependent)
  public static getDateTimeOfBooking(booking: Booking, localeId: string): string {
    const date = DateUtils.parseYYYYMMDD(BookingUtils.getDateOfSalonDate(booking.salonDate));
    const formattedDate = DateUtils.formatDateLocale(date, localeId, 'long');
    const formattedTime = DateUtils.addColonToTimeString(BookingUtils.getTimeOfExpertTime(booking.expertTime));
    return `${formattedDate} - ${formattedTime}`;
  }

  public static updateTime(expertTime: ExpertTime, time: StringHHmm): ExpertTime {
    return BookingUtils.assembleExpertTime(BookingUtils.getExpertIdOfExpertTime(expertTime), time);
  }

  public static updateDate(salonDate: SalonDate, date: StringYYYYMMDD): SalonDate {
    return BookingUtils.assembleSalonDate(BookingUtils.getSalonIdOfSalonDate(salonDate), date);
  }

  public static toBookingLocalDateTimeString(salonDate: SalonDate, expertTime: ExpertTime): string {
    const slot = BookingUtils.getSlotYYYYMMDDHHmmOfSalonDateAndExpertTime(salonDate, expertTime);
    const date = DateUtils.addDashToStringYYYYMMDD(DateUtils.getDatePartYYYYMMDD(slot)); // YYYY-MM-DD
    const time = DateUtils.addColonToTimeString(DateUtils.getTimePartHHmm(slot)); // HH:mm
    return `${date}T${time}:00`;
  }

  public static getYYYYMMDDHHMMEpoch(dateYYYYMMDD: StringYYYYMMDD, timeHHmm: StringHHmm, timezone = 'UTC'): number {
    const utcEpochMs = Date.UTC(
      +dateYYYYMMDD.substring(0, 4),
      +dateYYYYMMDD.substring(4, 6) - 1,
      +dateYYYYMMDD.substring(6, 8),
      +timeHHmm.substring(0, 2),
      +timeHHmm.substring(2, 4),
    );
    const offsetMs = BookingUtils.getUtcOffsetMs(utcEpochMs, timezone);
    return (utcEpochMs - offsetMs) / 1000;
  }

  public static getBookingDateTimeEpoch(salonDate: SalonDate, expertTime: ExpertTime, timezone = 'UTC'): number {
    const date = BookingUtils.getDateOfSalonDate(salonDate);
    const time = BookingUtils.getTimeOfExpertTime(expertTime);
    const utcEpochMs = Date.UTC(
      +date.substring(0, 4),
      +date.substring(4, 6) - 1,
      +date.substring(6, 8),
      +time.substring(0, 2),
      +time.substring(2, 4),
    );
    const offsetMs = BookingUtils.getUtcOffsetMs(utcEpochMs, timezone);
    return (utcEpochMs - offsetMs) / 1000;
  }

  private static getUtcOffsetMs(epochMs: number, timezone: string): number {
    const localIso = new Date(epochMs).toLocaleString('sv', { timeZone: timezone }); // => 'YYYY-MM-DD HH:mm:ss'
    const localEpochMs = new Date(localIso + 'Z').getTime();
    return localEpochMs - epochMs;
  }

  public static epochToIso8601Timezoneless(epoch: number): string {
    return new Date(epoch * 1000).toISOString().slice(0, 19);
  }
}
